/**
 * Hook for listening to real-time supply request events on the default Socket.io namespace.
 * The backend auto-joins authenticated users into tenant:{tenantId}:supply-requests room,
 * so this hook only needs to register event listeners.
 *
 * Directly invalidates React Query caches AND updates the Zustand store
 * so both list pages (React Query) and any store-dependent components stay in sync.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '@/store/tenant.store';
import { useSupplyRequestStore } from '@/store/supply-request.store';
import { useSocketFactory } from './useSocketFactory';

// Supply request event types (mirrors backend SupplyRequestEventType)
export type SupplyRequestEventType =
  | 'supply-request:created'
  | 'supply-request:updated'
  | 'supply-request:status-changed'
  | 'supply-request:deleted';

// Supply request event payload (mirrors backend SupplyRequestEvent)
export interface SupplyRequestEvent {
  type: SupplyRequestEventType;
  requestId: string;
  tenantId: string;
  incidentId?: string;
  userId: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

export interface UseSupplyRequestSocketOptions {
  onCreated?: (event: SupplyRequestEvent) => void;
  onUpdated?: (event: SupplyRequestEvent) => void;
  onStatusChanged?: (event: SupplyRequestEvent) => void;
  onDeleted?: (event: SupplyRequestEvent) => void;
  enabled?: boolean;
}

export interface UseSupplyRequestSocketReturn {
  isConnected: boolean;
}

/** React Query key prefix for supply request queries. */
const SUPPLY_REQUESTS_KEY_PREFIX = ['supply-requests'] as const;

export function useSupplyRequestSocket(
  options: UseSupplyRequestSocketOptions = {}
): UseSupplyRequestSocketReturn {
  const { enabled = true } = options;
  const { activeTenant } = useTenantStore();
  const queryClient = useQueryClient();

  /** Invalidate React Query caches + update Zustand store + call consumer callback. */
  const handleEvent = (type: string, cb?: (e: SupplyRequestEvent) => void) => (event: SupplyRequestEvent) => {
    // Invalidate React Query so list/detail pages refetch
    // useTenantQuery prepends activeTenant.id to query keys, so we must match
    const tenantId = activeTenant?.id;
    queryClient.invalidateQueries({ queryKey: tenantId ? [tenantId, ...SUPPLY_REQUESTS_KEY_PREFIX] : [...SUPPLY_REQUESTS_KEY_PREFIX] });
    // Keep Zustand store in sync for any store-dependent components
    useSupplyRequestStore.getState().handleSocketEvent(type, event.requestId);
    cb?.(event);
  };

  const { isConnected } = useSocketFactory({
    namespace: '',
    displayName: 'Supply Request',
    enabled,
    incidentAware: false,
    showToasts: false,
    extraAuth: activeTenant ? { tenantId: activeTenant.id } : undefined,
    events: {
      'supply-request:created': handleEvent('created', options.onCreated),
      'supply-request:updated': handleEvent('updated', options.onUpdated),
      'supply-request:status-changed': handleEvent('status-changed', options.onStatusChanged),
      'supply-request:deleted': handleEvent('deleted', options.onDeleted),
    },
  });

  return { isConnected };
}
